import React, { useEffect, useState } from 'react';
import { ItemImgProps } from '@v2/pages/machines/common/types/props';
import { ImageUpload } from '@helpers';
import { KEYS_TAGS } from '@components/imageHandling/common';
import TouchableElement from '@components/common/touchableElement';
import { View } from 'react-native';
import { scale } from '@components/frame';
import Svg from '@styles/icons/svg';
import { RenderImage } from './RenderImage';
import { useNavigationCheck } from '@v2/pages/machines/hooks/useNavigationCheck';
import { IMAGE_UPLOAD_HEADER_TEXT } from '@v2/pages/common/constants/base.constants';
import { useBuildLinkListByHeaderConfigAndNavigationContext } from '@v2/pages/common/hooks/useBuildLinkListByHeaderConfigAndNavigationContext';
import { useHandleImageNavigation } from '@v2/pages/common/hooks/useHandleImageNavigation';
import { useHandleMainDetailsScreenNavigation } from '@v2/pages/common/hooks/useHandleMainDetailsScreenNavigation';
import { NCFileDetailsScreenName } from '@v2/pages/nc-file/details/navigation/config';
import { NCFileDetailsScreen } from '@v2/pages/nc-file/details/common/enums/ncFileDetails.enum';
import { LevelSubScreen, Perspective } from '@v2/pages/common/enums';
import { PartDetailsScreenName } from '@v2/pages/part/details/navigation/config';
import { PartDetailsScreen } from '@v2/pages/part/details/common/enums/partDetails.enum';

interface Props extends ItemImgProps {}

export const ItemImg = React.memo(
  ({
    C,
    ncFile,
    ncFileId,
    ncFileStatus,
    imageHash,
    partId,
    partName,
    imageData = {},
    imageDetails = [],
    selected,
    isProcessing,
    imageRandomHash,
    generateImageURL,
    loading: globalLoading,
    machineId,
    documentId,
    shortName,
  }: Props) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [uploadedImage, setUploadedImage] = useState<Blob | null>(null);
    const [imageLink, setImageLink] = useState<string | null>(null);
    const details = imageDetails?.find((i) => imageData?.pageNumber === i.pageNumber);

    const { safeIsNowSubScreenCheck } = useNavigationCheck();
    const { handleImageNavigation } = useHandleImageNavigation();
    const { navigateToMainDetailsScreen } = useHandleMainDetailsScreenNavigation();

    useEffect(() => {
      const localData = imageData as { local?: boolean; url?: string } | null | undefined;
      if (localData?.local && localData?.url) {
        setImageLink(localData.url);
        return;
      }
      (async () => {
        if (imageHash && imageData) {
          const _imageLink = await generateImageURL({
            size: '_1x',
            cropped: imageData?.isCropped,
            item: { imageHash, extensionDerivative: imageData?.extensionDerivative },
            page: imageData?.pageNumber,
          });
          setImageLink(_imageLink);
        }
      })();
    }, [imageHash]);

    useEffect(() => {
      if (imageHash) setUploadedImage(null);
    }, [imageHash]);

    const possibleAddImage = ncFileId && !imageHash && !uploadedImage && !isProcessing;

    const imageDocumentReferences = useBuildLinkListByHeaderConfigAndNavigationContext({
      selectedMachine: { machineId, shortName },
      shouldLinkOnlyMachine: true,
    });

    const uploadImageFunction = () => {
      if (loading || !possibleAddImage) {
        return;
      }

      ImageUpload({
        headerText: IMAGE_UPLOAD_HEADER_TEXT.AddToMachine,
        headerDescription: shortName,
        ...(safeIsNowSubScreenCheck ? { ncFile } : {}),
        uploadStart: () => setLoading(true),
        uploadEnd: (img: Blob) => {
          setLoading(false);
          setUploadedImage(img);
        },
        imageDocumentReferences,
        tags: [
          {
            scopeId: KEYS_TAGS.Machine,
            scopeReferenceId: machineId,
            isFavorite: true,
          },
          ...(safeIsNowSubScreenCheck
            ? [
                {
                  scopeId: KEYS_TAGS.Clamping,
                  scopeReferenceId: ncFileId,
                  isFavorite: true,
                },
              ]
            : []),
        ],
      });
    };

    const handleImagePress = () => {
      if (possibleAddImage) {
        uploadImageFunction();
        return;
      }
      if (documentId) {
        handleImageNavigation({ documentId });
        return;
      }

      if (imageHash) {
        const isNcFileImage = imageData?.scopeId === KEYS_TAGS.Clamping;
        const isPartImage = imageData?.scopeId === KEYS_TAGS.Part;

        if (isNcFileImage) {
          navigateToMainDetailsScreen({
            mainScreen: NCFileDetailsScreenName,
            subScreen: NCFileDetailsScreen.NC_FILE,
            levelSubScreen: LevelSubScreen.DOCUMENTS,
            params: {
              perspective: Perspective.NC_FILE,
              ncFileId: ncFileId,
              ncFileName: ncFile,
              ncFileStatus: ncFileStatus,
              selectedImageKey: imageHash,
            },
          });
          return;
        }

        if (isPartImage) {
          navigateToMainDetailsScreen({
            mainScreen: PartDetailsScreenName,
            subScreen: PartDetailsScreen.PART,
            levelSubScreen: LevelSubScreen.DOCUMENTS,
            params: {
              perspective: Perspective.PART,
              partId,
              partName,
              selectedImageKey: imageHash,
            },
          });
          return;
        }
      }
    };

    return (
      <TouchableElement onPress={handleImagePress}>
        {({ boxStyle }) => (
          <View
            style={[
              possibleAddImage ? boxStyle : {},
              {
                width: scale(90),
                height: scale(60),
                position: 'relative',
                justifyContent: 'center',
                alignContent: 'center',
                alignItems: 'center',
              },
            ]}
          >
            <View
              style={{
                width: scale(90),
                height: scale(60),
                backgroundColor: C('Overlay/Light/Dim very light'),
                borderRadius: scale(8),
                opacity: 0.5,
                position: 'absolute',
              }}
            />
            {/* {isProcessing && (
              <View
                style={{
                  width: '100%',
                  height: '100%',
                  position: 'absolute',
                  zIndex: 10000,
                  backgroundColor: C('Background/Light/System'),
                }}
              >
                <View
                  style={{
                    backgroundColor: C('Picture/Light/Overlay'),
                    justifyContent: 'center',
                    alignContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: '100%',
                  }}
                >
                  <Svg icon={'pdf'} width={scale(23)} height={scale(29)} color="Label/Light/Quaternary" />
                  <Text style={[font.Footnote, { color: C('Label/Light/Quaternary') }]}>{i18n.t('Processing...')}</Text>
                </View>
              </View>
            )} */}
            {globalLoading && (
              <View
                style={{
                  width: '100%',
                  height: '100%',
                  position: 'absolute',
                  zIndex: 10000,
                  backgroundColor: C('Background/Light/System'),
                }}
              >
                <View
                  style={{
                    backgroundColor: C('Picture/Light/Overlay'),
                    justifyContent: 'center',
                    alignContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: '100%',
                  }}
                />
              </View>
            )}
            {possibleAddImage ? (
              <TouchableElement onPress={uploadImageFunction}>
                {({ boxStyle: _boxStyle, hovered }) => (
                  <View
                    style={[
                      _boxStyle,
                      {
                        backgroundColor: hovered ? C(selected ? 'Button/Light/Secondary hover' : 'Button/Light/Secondary hover') : 'transparent',
                        borderRadius: scale(4),
                        padding: scale(8),
                      },
                    ]}
                  >
                    <Svg
                      icon="addPicture"
                      color={selected ? (hovered ? 'Button/Light/Primary Text' : 'Label/Light/Always Light Tertiary') : 'Button/Light/Secondary Text'}
                      width={scale(45)}
                      height={scale(34)}
                      key={'image-icon-' + selected}
                    />
                  </View>
                )}
              </TouchableElement>
            ) : (
              <RenderImage
                {...{
                  uploadedImage,
                  imageLink,
                  imageRandomHash,
                  rotationAngle: imageData?.isCropped ? 0 : details?.rotationAngle,
                  C,
                  borderRadius: scale(8),
                }}
              />
            )}
            {!possibleAddImage && (
              <View
                style={{
                  width: scale(90),
                  height: scale(60),
                  backgroundColor: C('Overlay/Light/Dim light'),
                  opacity: 0.5,
                  borderRadius: scale(8),
                  position: 'absolute',
                }}
              />
            )}
          </View>
        )}
      </TouchableElement>
    );
  },
  (prev, next) =>
    prev.imageHash === next.imageHash &&
    prev.loading === next.loading &&
    prev.imageData === next.imageData &&
    prev.imageRandomHash === next.imageRandomHash &&
    prev.C.scheme === next.C.scheme &&
    prev.ncFile === next.ncFile &&
    prev.ncFileId === next.ncFileId &&
    prev.ncFileStatus === next.ncFileStatus &&
    prev.documentId === next.documentId &&
    prev.shortName === next.shortName &&
    prev.selected === next.selected &&
    prev.isProcessing === next.isProcessing &&
    prev.imageRandomHash === next.imageRandomHash &&
    prev.machineId === next.machineId &&
    prev.partId === next.partId &&
    prev.partName === next.partName
);
